import { motion } from "framer-motion";
import type { ReactNode } from "react";

import { CheckIcon, Clock, HelpCircle, XCircle } from "../../../assets/icons/interface/interface";
import { useJobSearchStatus } from "../../../hooks/useJobSearchStatus";
import { findJobSearchStatus, type JobSearchStatusTone, jobSearchStatusOptions } from "../../../seed/job-search-status";
import { Button } from "../../JobsPage/components/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./DropdownMenu";

const TONE_ICONS: Record<JobSearchStatusTone, ReactNode> = {
	positive: <CheckIcon className="h-4 w-4 text-green-500" />,
	caution: <Clock className="h-4 w-4 text-yellow-500" />,
	neutral: <XCircle className="h-4 w-4 text-red-500" />,
};

const UNSET_LABEL = "Set job status";

/**
 * The job-search status control in the applicant nav.
 *
 * It keeps no state of its own. It reads and writes the account cache through useJobSearchStatus,
 * so a pick here also moves the profile card and the preferences modal, and a rejected save leaves
 * the badge showing what is actually stored rather than what the user tried to store.
 */
const JobStatusBadge = () => {
	const { status, setStatus } = useJobSearchStatus();
	const current = findJobSearchStatus(status);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="outline"
					className="flex items-center space-x-2 px-4 py-2 rounded-full border-2 hover:bg-gray-100 transition-colors duration-200"
				>
					<span className="font-medium text-sm">{current?.label || UNSET_LABEL}</span>
					<span className="animate-pulse">
						{current ? TONE_ICONS[current.tone] : <HelpCircle className="h-4 w-4 text-gray-500" />}
					</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent asChild>
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					exit={{ opacity: 0, y: -10 }}
					transition={{ duration: 0.2 }}
					className="w-72 p-2 rounded-lg shadow-lg bg-white"
				>
					{jobSearchStatusOptions.map((option) => (
						<DropdownMenuItem
							key={option.value}
							onSelect={() => setStatus(option.value)}
							className="flex flex-col items-start py-3 px-4 hover:bg-gray-100 rounded-md transition-colors duration-200"
						>
							<div className="flex items-center font-medium text-sm">
								{TONE_ICONS[option.tone]}
								<span className="ml-2">{option.label}</span>
							</div>
							<p className="text-xs text-gray-600 mt-1 pl-6">{option.description}</p>
						</DropdownMenuItem>
					))}
				</motion.div>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default JobStatusBadge;
